(() => {
  if (window.YuumaeCart) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const views = () => [...document.querySelectorAll('yuumae-cart-drawer, yuumae-cart-page')];
  const sectionIds = () => views().map(view => view.dataset.sectionId).filter(Boolean);
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`yuumae:${name}`, { detail }));
  const message = key => views()[0]?.dataset[key] || 'Impossible de mettre à jour le panier. Réessayez.';
  const hasLineProperties = item => Object.keys(item.properties || {}).some(key => key !== '_yuumae_cart_subscription');
  let pending = Promise.resolve();
  let jobs = 0;

  const enqueue = (task, locksUI = true) => {
    if (locksUI) { jobs += 1; emit('cart-busy', true); }
    const next = pending.then(task).finally(() => { if (locksUI) { jobs -= 1; emit('cart-busy', jobs > 0); } });
    pending = next.catch(() => {});
    return next;
  };
  async function jsonRequest(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, { cache: 'no-store', ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : message('error'));
      return result;
    } catch (error) {
      if (error.name === 'TypeError' || error.name === 'AbortError' || error.name === 'SyntaxError') throw new Error(message('error'));
      throw error;
    } finally { clearTimeout(timeout); }
  }
  const read = () => jsonRequest(`${root()}cart.js`);
  async function sectionHTML(ids) {
    if (!ids.length) return {};
    const url = new URL(location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', ids.join(','));
    return jsonRequest(url.toString(), { headers: { Accept: 'text/html' } });
  }
  function sectionContent(html) {
    if (typeof html !== 'string') return null;
    const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-content]');
    return content && !content.textContent.includes('Liquid error') ? content : null;
  }
  async function sync(result = {}) {
    const ids = sectionIds();
    const sections = { ...result.sections };
    const missing = ids.filter(id => !sectionContent(sections[id]));
    let cart = typeof result.item_count === 'number' ? result : null;
    const results = await Promise.allSettled([sectionHTML(missing), cart ? Promise.resolve(cart) : read()]);
    if (results[0].status === 'fulfilled') Object.assign(sections, results[0].value);
    if (results[1].status === 'fulfilled') cart = results[1].value;
    const retry = ids.filter(id => !sectionContent(sections[id]));
    if (retry.length) {
      try { Object.assign(sections, await sectionHTML(retry)); } catch (_) {}
    }
    if (cart) {
      document.querySelectorAll('[data-cart-count]').forEach(element => { element.textContent = cart.item_count; });
      emit('cart-update', cart);
    }
    emit('cart-render', sections);
    return cart;
  }
  async function mutate(endpoint, payload) {
    const ids = sectionIds();
    let body;
    let headers = {};
    if (payload instanceof FormData) {
      body = payload;
      if (ids.length) { body.set('sections', ids.join(',')); body.set('sections_url', location.pathname); }
    } else {
      body = JSON.stringify({ ...payload, ...(ids.length ? { sections: ids.join(','), sections_url: location.pathname } : {}) });
      headers = { 'Content-Type': 'application/json' };
    }
    return jsonRequest(`${root()}cart/${endpoint}.js`, { method: 'POST', body, headers });
  }
  async function guarded(task) {
    try { return await task(); }
    catch (error) { await sync(); throw error; }
  }
  window.YuumaeCart = {
    refresh: () => enqueue(() => sync()),
    add: data => enqueue(() => guarded(async () => {
      const result = await mutate('add', data);
      await sync(result);
      return result;
    })),
    updateNote: note => enqueue(() => guarded(async () => {
      const result = await mutate('update', { note });
      await sync(result);
      return result;
    }), false),
    change: (key, quantity, sellingPlan) => enqueue(() => guarded(async () => {
      let payload = { id: key, quantity };
      if (sellingPlan !== undefined) {
        const cart = await read();
        const index = cart.items.findIndex(item => item.key === key);
        if (index < 0) throw new Error(message('error'));
        const item = cart.items[index];
        if (!sellingPlan && item.selling_plan_allocation && item.properties?._yuumae_cart_subscription !== '1') throw new Error(message('error'));
        payload = { line: index + 1, quantity: item.quantity, selling_plan: sellingPlan || null };
        if (sellingPlan && !item.selling_plan_allocation) payload.properties = { ...item.properties, _yuumae_cart_subscription: '1' };
      }
      const result = await mutate('change', payload);
      await sync(result);
      return result;
    })),
    replace: (key, variantId) => enqueue(() => guarded(async () => {
      const before = await read();
      const original = before.items.find(item => item.key === key);
      if (!original || original.quantity !== 1 || original.selling_plan_allocation || hasLineProperties(original) || before.items.some(item => item.variant_id === variantId)) throw new Error(message('error'));
      const added = await mutate('add', { items: [{ id: variantId, quantity: 1 }] });
      try {
        const current = await read();
        const candidates = current.items.filter(item => item.variant_id === original.variant_id && item.quantity === 1 && !item.selling_plan_allocation && !hasLineProperties(item) && (item.properties?._yuumae_cart_subscription || '') === (original.properties?._yuumae_cart_subscription || ''));
        if (candidates.length !== 1) throw new Error(message('replaceError'));
        const result = await mutate('change', { id: candidates[0].key, quantity: 0 });
        await sync(result);
      } catch (_) {
        await sync();
        throw new Error(message('replaceError'));
      }
      return added;
    }))
  };

  class YuumaeCartView extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.disabledStates = new Map();
      this.setAttribute('data-enhanced', '');
      document.addEventListener('yuumae:cart-render', event => {
        const html = event.detail[this.dataset.sectionId];
        if (html) this.render(html);
        else this.error(this.dataset.refreshError);
      }, options);
      document.addEventListener('yuumae:cart-busy', event => this.setBusy(event.detail), options);
      this.addEventListener('click', event => {
        const summary = event.target.closest('[data-cart-subscription]>summary');
        if (summary) {
          requestAnimationFrame(() => {
            const panel = summary.parentElement;
            const area = this.querySelector('[data-cart-scroll]');
            if (!panel.open || !area) return;
            const overflow = panel.getBoundingClientRect().bottom - area.getBoundingClientRect().bottom + 16;
            if (overflow > 0) area.scrollBy({ top: overflow, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          });
        }
        const remove = event.target.closest('[data-cart-remove]');
        const quantity = event.target.closest('[data-cart-quantity-change]');
        const replace = event.target.closest('[data-cart-replace]');
        const subscribe = event.target.closest('[data-cart-plan-apply]');
        const revert = event.target.closest('[data-cart-plan-revert]');
        if (!remove && !quantity && !replace && !subscribe && !revert) return;
        event.preventDefault();
        if (this.busy) return;
        if (replace) { this.run(() => window.YuumaeCart.replace(replace.dataset.key, Number(replace.dataset.variantId))); return; }
        if (revert) {
          const row = revert.closest('[data-cart-line]');
          this.run(() => window.YuumaeCart.change(row.dataset.key, Number(row.dataset.quantity), ''));
          return;
        }
        if (subscribe) {
          const row = subscribe.closest('[data-cart-line]');
          const choice = row.querySelector('[data-cart-plan-select]');
          if (choice?.value) this.run(() => window.YuumaeCart.change(row.dataset.key, Number(row.dataset.quantity), choice.value));
          return;
        }
        const row = (remove || quantity).closest('[data-cart-line]');
        const input = row.querySelector('[data-cart-quantity]');
        const step = Number(input.step) || 1;
        let value = remove ? 0 : Number(row.dataset.quantity) + Number(quantity.dataset.cartQuantityChange) * step;
        if (value < Number(input.min)) value = 0;
        this.run(() => window.YuumaeCart.change(row.dataset.key, value));
      }, options);
      this.addEventListener('change', event => {
        if (this.busy) return;
        const input = event.target;
        if (input.matches('[data-cart-note]')) { this.run(() => window.YuumaeCart.updateNote(input.value)); return; }
        const row = input.closest('[data-cart-line]');
        if (input.matches('[data-cart-quantity]')) {
          if (!input.checkValidity() || !Number.isInteger(Number(input.value))) { input.reportValidity(); input.value = row.dataset.quantity; return; }
          if (Number(input.value) !== Number(row.dataset.quantity)) this.run(() => window.YuumaeCart.change(row.dataset.key, Number(input.value)));
        }
        if (input.matches('[data-cart-plan-select]')) this.previewSubscription(input.closest('[data-cart-subscription]'));
      }, options);
    }
    previewSubscription(panel) {
      const option = panel.querySelector('[data-cart-plan-select]').selectedOptions[0];
      if (!option) return;
      panel.querySelector('[data-cart-subscription-price]').textContent = option.dataset.price;
      const badge = panel.querySelector('[data-cart-saving]');
      const saving = Number(option.dataset.saving);
      badge.textContent = saving > 0 ? `−${saving}\u00a0%` : '';
      badge.hidden = !(saving > 0);
    }
    rememberFocus() {
      const active = document.activeElement;
      if (!this.querySelector('[data-cart-content]')?.contains(active)) return;
      const row = active.closest('[data-cart-line]');
      this.bookmark = { key: active.dataset.cartFocus, index: row?.dataset.index, variant: row?.dataset.variantId, revert: active.matches('[data-cart-plan-revert]'), plan: active.matches('[data-cart-plan-apply]') ? row.querySelector('[data-cart-plan-select]').value : null };
    }
    restoreFocus() {
      if ((this.dialog && !this.dialog.open) || !this.bookmark) return;
      const { key, index, variant, plan, revert } = this.bookmark;
      const exact = key && [...this.querySelectorAll('[data-cart-focus]')].find(element => element.dataset.cartFocus === key);
      const rows = [...this.querySelectorAll('[data-cart-line]')];
      const row = ((plan || revert) && rows.find(element => element.dataset.variantId === variant && element.dataset.sellingPlan === (plan || '') && element.dataset.cartUpgraded === 'true')) || rows.find(element => element.dataset.index === index);
      const target = exact || (plan ? row?.querySelector('[data-cart-subscription-status]') : revert ? row?.querySelector('.yuumae-cart-plans>summary') : null) || row?.querySelector('[data-cart-quantity]') || this.querySelector('[data-cart-quantity], [data-cart-empty-heading]') || this.querySelector('[data-cart-close]');
      target?.focus({ preventScroll: true });
      this.bookmark = null;
    }
    setBusy(busy) {
      busy = Boolean(busy);
      if (busy && !this.busy) this.rememberFocus();
      this.busy = busy;
      this.querySelector('[data-cart-content]')?.setAttribute('aria-busy', String(busy));
      if (busy) {
        this.querySelectorAll('[data-cart-mutate], yuumae-product-form button[type=submit]').forEach(control => {
          if (!this.disabledStates.has(control)) this.disabledStates.set(control, control.disabled);
          control.disabled = true;
        });
      } else {
        this.disabledStates.forEach((disabled, control) => { if (control.isConnected) control.disabled = disabled; });
        this.disabledStates.clear();
        this.restoreFocus();
      }
    }
    render(html) {
      const next = sectionContent(html);
      if (!next) { this.error(this.dataset.refreshError); return; }
      this.clearError();
      this.rememberFocus();
      const note = this.querySelector('[data-cart-note]');
      const draftNote = note && note.value !== note.defaultValue ? note.value : null;
      const subscriptionDrafts = [...this.querySelectorAll('[data-cart-subscription]')].map(panel => ({ key: panel.closest('[data-cart-line]').dataset.key, open: panel.open, plan: panel.querySelector('[data-cart-plan-select]').value }));
      const scrollTop = this.querySelector('[data-cart-scroll]')?.scrollTop || 0;
      this.querySelector('[data-cart-content]').replaceWith(next);
      if (draftNote !== null && next.querySelector('[data-cart-note]')) next.querySelector('[data-cart-note]').value = draftNote;
      next.querySelectorAll('[data-cart-subscription]').forEach(panel => {
        const draft = subscriptionDrafts.find(value => value.key === panel.closest('[data-cart-line]').dataset.key);
        if (!draft) return;
        const select = panel.querySelector('[data-cart-plan-select]');
        if ([...select.options].some(option => option.value === draft.plan)) select.value = draft.plan;
        panel.open = draft.open;
        this.previewSubscription(panel);
      });
      const count = Number(next.dataset.count);
      const countLabel = this.querySelector('[data-drawer-count]');
      if (countLabel) countLabel.textContent = count ? `(${count})` : '';
      document.querySelectorAll('[data-cart-count]').forEach(element => { element.textContent = count; });
      const scrollArea = this.querySelector('[data-cart-scroll]');
      if (scrollArea) scrollArea.scrollTop = scrollTop;
      this.setBusy(this.busy);
      this.querySelector('[data-cart-live]').textContent = this.dataset.updated;
    }
    error(text) {
      const error = this.querySelector('[data-cart-error]');
      error.textContent = text;
      const fallback = document.createElement('a');
      fallback.href = `${root()}cart`;
      fallback.textContent = this.dataset.cartLink || 'Voir le panier';
      error.append(fallback); error.hidden = false;
    }
    clearError() { this.querySelector('[data-cart-error]').hidden = true; }
    async run(task) {
      this.clearError();
      try { await task(); } catch (error) { this.error(error.message || this.dataset.error); }
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  class YuumaeCartPage extends YuumaeCartView {}
  class YuumaeCartDrawer extends YuumaeCartView {
    connectedCallback() {
      super.connectedCallback();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')].filter(element => element.tabIndex >= 0 && !element.disabled && !element.closest('[inert]') && element.getClientRects().length);
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !controls.includes(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }, options);
      this.dialog.addEventListener('close', () => this.restoreTrigger(), options);
      this.dialog.addEventListener('pointerdown', event => { this.backdropDown = this.outside(event); }, options);
      this.dialog.addEventListener('click', event => { if (this.backdropDown && this.outside(event)) this.close(); this.backdropDown = false; }, options);
      this.querySelector('[data-cart-close]').addEventListener('click', () => this.close(), options);
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-cart-open]');
        if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !this.dialog.showModal) return;
        event.preventDefault(); this.open(trigger, true);
      }, options);
      document.addEventListener('yuumae:cart-added', event => this.open(event.detail.trigger || document.activeElement, false), options);
      document.addEventListener('shopify:section:select', event => { if (event.target.contains(this)) this.open(document.activeElement, false); }, options);
      window.addEventListener('pageshow', event => { if (event.persisted) window.YuumaeCart.refresh(); }, options);
      document.querySelectorAll('[data-cart-open]').forEach(trigger => { trigger.setAttribute('aria-haspopup', 'dialog'); trigger.setAttribute('aria-controls', this.dialog.id); });
    }
    outside(event) {
      const rect = this.dialog.getBoundingClientRect();
      return event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
    }
    open(trigger, refresh) {
      if (!this.dialog.showModal) return;
      this.animation?.cancel();
      if (!this.dialog.open) {
        this.trigger = trigger;
        document.querySelectorAll('yuumae-header details[open]').forEach(menu => { menu.open = false; });
        this.dialog.showModal();
        if (!matchMedia('(prefers-reduced-motion: reduce)').matches) this.animation = this.dialog.animate({ transform: ['translateX(100%)', 'translateX(0)'] }, { duration: 260, easing: 'cubic-bezier(.2,.8,.2,1)' });
      }
      if (refresh) { this.clearError(); window.YuumaeCart.refresh(); }
    }
    close() {
      if (!this.dialog.open) return;
      this.animation?.cancel();
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { this.dialog.close(); return; }
      this.animation = this.dialog.animate({ transform: ['translateX(0)', 'translateX(100%)'] }, { duration: 200, easing: 'cubic-bezier(.2,.8,.2,1)' });
      this.animation.onfinish = () => this.dialog.close();
    }
    restoreTrigger() {
      const fallback = document.querySelector('[data-cart-open]');
      const target = this.trigger?.isConnected && !this.trigger.disabled && !this.contains(this.trigger) ? this.trigger : fallback;
      target?.focus({ preventScroll: true });
      this.bookmark = null;
    }
    disconnectedCallback() {
      this.abort?.abort(); this.animation?.cancel();
      if (this.dialog?.open) this.dialog.close();
    }
  }
  if (!customElements.get('yuumae-cart-page')) customElements.define('yuumae-cart-page', YuumaeCartPage);
  if (!customElements.get('yuumae-cart-drawer')) customElements.define('yuumae-cart-drawer', YuumaeCartDrawer);
})();
